import React, { useEffect, useState } from 'react'

import { DateTime } from 'luxon'
import { useDispatch, useSelector } from 'react-redux'
import { actions, selectors, states } from 'modules/submissions'
import { useUserContext } from 'components'
import { fileStatusById, fileStatusByKey } from 'src/constants/FileStatus'
import { requestSignOffFormFileDownload } from '../../utils'
import { getActivityLogEntries } from 'services/api/collections'
import { AL_UPLOADED_VIA_UI } from 'src/constants/activityLogTypeIds'
import {
    generatePath,
    useNavigate,
    useLocation,
    useOutletContext,
} from 'react-router-dom'
import { PATHS } from 'src/constants/constants'
import { formatTestId } from 'src/utils/formatTestId/formatTestId'
import { RestrictAccess } from 'src/components/index'
import AccessDenied from 'src/components/AccessDenied/AccessDenied'
import { useStepper } from '../Stepper/StepperContext'
import { useQuery } from '@tanstack/react-query'

import {
    Button,
    Checkbox,
    FormControlLabel,
    Paper,
    CircularProgress,
} from '@mui/material'
import { useMemo } from 'react'

import styles from './signOff.module.scss'

const SignOff = () => {
    const history = useNavigate()
    const location = useLocation()
    const { roles, isProvider } = useUserContext()
    const [isAdmin, setIsAdmin] = useState(false)
    const {
        data: submission,
        submissionId,
        setPageTitle,
        getSubmission,
    } = useOutletContext()
    const [termsChecked, setTermsChecked] = React.useState(false)
    const [loading, setLoading] = React.useState(false)
    const isHesaAdmin = roles.includes('admin')
    const isProviderSignOff = isProvider && roles.includes('sign-off')
    const canSignOff = submission.isLatest && (isHesaAdmin || isProviderSignOff)
    const dispatch = useDispatch()
    const { navigateToStep } = useStepper()
    const updateStatus = useSelector(selectors.getUpdateWorkingSubStatus)

    const { data: activityLog, isFetching: activityLogLoading } = useQuery({
        queryKey: ['activity-log-uploaded', submission?.uuid],
        queryFn: () =>
            getActivityLogEntries({
                submissionUuid: submission?.uuid,
                activityLogTypeId: AL_UPLOADED_VIA_UI,
            }),
        enabled: Boolean(submission?.uuid),
        refetchInterval: false,
    })

    const user = useMemo(
        () => activityLog?.records?.at(0)?.username,
        [activityLog],
    )

    useEffect(() => {
        setPageTitle('Sign Off')
    }, [setPageTitle])

    const HandleSubmit = () => {
        if (signOffRequested()) {
            handleDownload()
        }

        if (isSignedOff()) {
            const route = generatePath(
                isAdmin ? PATHS.MONITORING : PATHS.DASHBOARD,
            )
            history(route)
        }

        if (isApproved()) {
            setLoading(true)
            dispatch(
                actions.updateWorkingSubmission({
                    submissionId: submission.uuid,
                    statusId: fileStatusByKey('PROVIDER_SIGNOFF').id,
                    action: 'Provider sign-off',
                }),
            )
        }
    }

    useEffect(() => {
        if (updateStatus === states.update.UPDATED) {
            getSubmission()
            setLoading(false)
        }
    }, [getSubmission, updateStatus])

    const signOffRequested = () =>
        fileStatusById(submission?.status.id).order ===
        fileStatusByKey('PROVIDER_SIGNOFF').order

    const isApproved = () =>
        fileStatusById(submission?.status.id).order ===
        fileStatusByKey('APPROVED').order

    const isSignedOff = () =>
        fileStatusById(submission?.status.id).order ===
        fileStatusByKey('SIGNED_OFF').order

    const handleDownload = () => {
        requestSignOffFormFileDownload(submission.fileHash)
    }

    const canSubmit = () =>
        isSignedOff() || signOffRequested() || (canSignOff && termsChecked)

    useEffect(() => {
        if (location.pathname.includes('admin') && !isAdmin) {
            setIsAdmin(true)
        }
    }, [submission.uuid, isAdmin, location.pathname])

    useEffect(() => {
        if (
            fileStatusById(submission?.status.id).order ===
            fileStatusByKey('PROVIDER_SIGNOFF').order
        ) {
            setLoading(false)
        }
    }, [submission])

    return (
        <RestrictAccess
            allowPermissions={['collections.approval-and-sign-off']}
            render={() => <AccessDenied mt={8} />}
        >
            <div>
                <Paper className={styles.signOff}>
                    <h2 className={styles.heading}>Sign off</h2>{' '}
                    {isApproved() ? (
                        <>
                            {loading || activityLogLoading ? (
                                <div className={styles.loading}>
                                    <CircularProgress aria-label="Circular loading animation" />
                                </div>
                            ) : (
                                ''
                            )}

                            <p
                                className="margin--bottom-1"
                                data-test-id={formatTestId('submissionInfo')}
                            >
                                Your submission{' '}
                                <strong>{submission.fileName}</strong>, uploaded
                                on{' '}
                                <strong>
                                    {DateTime.fromISO(
                                        submission.uploaded,
                                        new Date(),
                                    ).toFormat('dd LLLL y')}
                                </strong>{' '}
                                by <strong>{user}</strong> with Submission ID{' '}
                                <strong>{submissionId.slice(-8)}</strong> and
                                File ID <strong>{submission.fileHash}</strong>{' '}
                                needs to be signed off by your provider&apos;s
                                Accountable Officer (England and Wales) or Head
                                of Provider (Scotland and Northern Ireland).
                            </p>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        className="hdp-override"
                                        checked={termsChecked}
                                        disabled={!canSignOff}
                                        data-test-id={
                                            canSignOff
                                                ? formatTestId('agree to terms')
                                                : null
                                        }
                                        onChange={() => {
                                            setTermsChecked(!termsChecked)
                                        }}
                                    />
                                }
                                label="I understand that this will start the sign-off procedure and will send via email, documentation and a notification to my provider's Accountable Officer (England and Wales) or Head of Provider (Scotland and Northern Ireland) that sign-off has been requested."
                            />
                        </>
                    ) : (
                        ''
                    )}
                    {signOffRequested() ? (
                        <>
                            <p
                                className="margin--bottom-1"
                                data-test-id={formatTestId('submissionInfo')}
                            >
                                Your submission{' '}
                                <strong>{submission.fileName}</strong>, uploaded
                                on{' '}
                                <strong>
                                    {DateTime.fromISO(
                                        submission.uploaded,
                                        new Date(),
                                    ).toFormat('dd LLLL y')}
                                </strong>{' '}
                                by <strong>{user}</strong> with Submission ID{' '}
                                <strong>{submissionId.slice(-8)}</strong> and
                                File ID <strong>{submission.fileHash}</strong>{' '}
                                needs to be signed off by your provider&apos;s
                                Accountable Officer (England and Wales) or Head
                                of Provider (Scotland and Northern Ireland).
                            </p>
                            <p>
                                You can download the sign-off form by clicking
                                the button below. A copy of this form has been
                                emailed to your provider&apos;s Accountable
                                Officer or Head of Provider.
                            </p>
                        </>
                    ) : (
                        ''
                    )}
                    {isSignedOff() ? (
                        <>
                            <p data-test-id={formatTestId('submissionInfo')}>
                                Your submission{' '}
                                <strong>{submission.fileName}</strong>, uploaded
                                on{' '}
                                <strong>
                                    {DateTime.fromISO(
                                        submission.uploaded,
                                        new Date(),
                                    ).toFormat('dd LLLL y')}
                                </strong>{' '}
                                by <strong>{user}</strong> with ID{' '}
                                <strong>{submissionId.slice(-8)}</strong> has
                                been signed off.
                            </p>
                        </>
                    ) : (
                        ''
                    )}
                </Paper>
                <div className={styles.buttons}>
                    <Button
                        className="hdp-override large"
                        data-test-id="submit"
                        disabled={!canSubmit()}
                        disableRipple={true}
                        variant="contained"
                        onClick={HandleSubmit}
                    >
                        {signOffRequested() ? 'Download Sign-off Form' : ''}
                        {isApproved() ? 'Request Sign-Off' : ''}
                        {isSignedOff()
                            ? isAdmin
                                ? 'Return to monitoring'
                                : 'Return to my Dashboard'
                            : ''}
                    </Button>
                    <Button
                        className="hdp-override large hdp-override--grey"
                        data-test-id="back"
                        disabled={false}
                        disableRipple={true}
                        variant="contained"
                        onClick={() => navigateToStep('Approval')}
                    >
                        Back
                    </Button>
                </div>
                <div className="margin--top-1">
                    <span>
                        Please contact{' '}
                        <a
                            href="mailto:liaison@hesa.ac.uk"
                            data-test-id="emailLiaison"
                        >
                            HESA Liaison
                        </a>{' '}
                        if a resubmission is required.
                    </span>
                </div>
            </div>
        </RestrictAccess>
    )
}

export default SignOff
